import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { m } from "framer-motion";
import { client } from "../../lib/sanity";
import { PortableText } from "@portabletext/react";
import { urlForImage, urlForImageWithVanity } from "../../lib/sanityImageUrl";
import ExplainLayout from "./ExplainLayout"; // You'll create this
import ExplainPortableTextComponents from "../../Components/ExplainPortableTextComponents";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const Explain = () => {
  const { slug } = useParams();
  const [explainItem, setExplainItem] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  console.log("Current URL:", window.location.href);
  console.log("Slug from useParams:", slug);
  console.log("Route matched - Explain component loaded");

  useEffect(() => {
    const fetchExplainItem = async () => {
      try {
        const query = `*[_type == "explain" && slug.current == $slug][0]{
      _id,
      title,
      slug,
      excerpt,
      mainImage {
        asset->{
          _id,
          url,
          originalFilename,
          title,
          description,
          altText,
          metadata {
            dimensions {
              width,
              height
            }
          }
        },
        caption,
        alt
      },
      body,
      category,           
      publishedAt,
      seo{
        metaTitle,
        metaDescription,
        seoKeywords,
        openGraph{
          title,
          description,
          image{
            asset->{
              _id,
              url,
              originalFilename,
              metadata {
                dimensions {
                  width,
                  height
                }
              }
            },
            caption,
            alt
          }
        }
      }
    }`;

        const fetchedItem = await client.fetch(query, { slug });
        if (fetchedItem) {
          setExplainItem(fetchedItem);
        } else {
          setError("Explanation not found");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(`Error fetching explanation: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchExplainItem();
  }, [slug]);

  // Prepare SEO data
  const itemTitle = explainItem?.title || "INAQ Dictionary";
  const itemExcerpt = explainItem?.excerpt || "";
  const itemImage = explainItem?.mainImage
    ? urlForImageWithVanity(explainItem.mainImage)
    : "";
  const itemSeoData = explainItem?.seo || null;
  const itemSlug = explainItem
    ? `/vysvetlujeme/${explainItem.slug.current}`
    : `/vysvetlujeme/${slug || ""}`;

  return (
    <ExplainLayout
      seo={itemSeoData}
      slug={itemSlug}
      defaultTitle={itemTitle}
      defaultDescription={itemExcerpt}
      defaultOgImage={itemImage}
    >
      {loading ? (
        <div className='max-w-[1280px] mx-auto px-[15px]'>
          <div>Loading explanation... Please wait.</div>
        </div>
      ) : error ? (
        <div className='max-w-[1280px] mx-auto px-[15px]'>
          <div>Error: {error}</div>
          <div>Attempted to load slug: {slug}</div>
        </div>
      ) : !explainItem ? (
        <div className='max-w-[1280px] mx-auto px-[15px]'>
          <div>Explanation not found</div>
        </div>
      ) : (
        <div className='max-w-[680px] mt-12 md:mt-8 pt-[80px] mx-auto'>
          <div className='text-md sm:mb-0 md:mb-2 text-gray-500 xxxl:mx-10 xxs:mx-4 block'>
            <Link to='/' className='hover:text-gray-700'>
              Dictionary
            </Link>
            {explainItem.category && (
              <>
                <span className='mx-2'>/</span>
                <span className='text-gray-700 cursor-default'>
                  {explainItem.category}
                </span>
              </>
            )}
          </div>

          <m.div
            className='visible overflow-hidden rounded-[6px] text-center p-0 xxxl:mx-10 xxs:mx-4'
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className='blog-title text-left font-[Oxanium] font-bold text-4xl text-darkgray mb-2 mt-3 sm:mt-2 md:mt-4 relative'>
              {explainItem.title}
            </h1>

            <div className='border-none py-1 mb-8'>
              <div className='flex items-center'>
                <span className='text-gray-500 text-sm cursor-default'>
                  {formatDate(explainItem.publishedAt)}
                </span>
              </div>
            </div>

            <div className='blog-image'>
              {explainItem.mainImage && (
                <figure>
                  <img
                    className='w-full'
                    src={urlForImageWithVanity(explainItem.mainImage)}
                    alt={
                      explainItem.mainImage.asset?.altText ||
                      explainItem.mainImage.asset?.title ||
                      explainItem.mainImage.alt ||
                      explainItem.title
                    }
                  />
                  {explainItem.mainImage.caption && (
                    <figcaption className='text-center font-rubik text-md text-gray-500 mt-3'>
                      {explainItem.mainImage.caption}
                    </figcaption>
                  )}
                </figure>
              )}
            </div>

            <div className='blog-text inline-block w-full'>
              <div className='relative mx-auto mt-12 text-[17px] text-gray-900 font-rubik lg:w-full'>
                <div className='text-left'>
                  <PortableText
                    value={explainItem.body}
                    components={ExplainPortableTextComponents}
                  />
                </div>
              </div>
            </div>
          </m.div>
        </div>
      )}
    </ExplainLayout>
  );
};

export default Explain;
